import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Button,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Check, Close } from "@mui/icons-material";
import { setSelectedCategory } from "../reducers/ruleFrameSlice";
import {
  fetchLatest100DaySummaries,
  fetchFilingRules,
  fetchSkipAnalysis,
  setSelected,
  updateFilingRuleStatus,
  deleteFilingRuleStatus,
  exportFilingRulesToExcel,
} from "../reducers/filingRulesSlice";

const getColumns = (summaries) => [
  {
    field: "final_count",
    flex: 3,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Category <br /> Number
      </div>
    ),
  },
  {
    field: "enabled",
    flex: 3,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Is <br /> Enabled
      </div>
    ),
  },
  {
    field: "is_advanced",
    flex: 3,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Is <br /> Advanced
      </div>
    ),

    renderCell: (params) =>
      params.value ? (
        <Check
          fontSize="small"
          sx={{ color: "rgba(76, 175, 80, 0.6)" }} // subtle green
        />
      ) : (
        <Close
          fontSize="small"
          sx={{ color: "rgba(158, 158, 158, 0.6)" }} // subtle gray
        />
      ),
  },
  {
    field: "needs_review",
    flex: 3,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Review & <br /> Approval
      </div>
    ),

    renderCell: (params) =>
      params.value ? (
        <Check
          fontSize="small"
          sx={{ color: "rgba(76, 175, 80, 0.6)" }} // subtle green
        />
      ) : (
        <Close
          fontSize="small"
          sx={{ color: "rgba(158, 158, 158, 0.6)" }} // subtle gray
        />
      ),
  },
  { field: "category_name", headerName: "Pathology Test Category", flex: 7 },
  { field: "sub_test_name", headerName: "Pathology Test", flex: 7 },
  {
    field: "skip_without_ranges",
    flex: 3,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Skip Without
        <br />
        Ranges
      </div>
    ),
    renderCell: (params) =>
      params.value ? (
        <Check
          fontSize="small"
          sx={{ color: "rgba(76, 175, 80, 0.6)" }} // subtle green
        />
      ) : (
        <Close
          fontSize="small"
          sx={{ color: "rgba(158, 158, 158, 0.6)" }} // subtle gray
        />
      ),
  },
  {
    field: "file_normal",
    flex: 2.5,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        File When <br /> Normal
      </div>
    ),
    renderCell: (params) =>
      params.value ? (
        <Check
          fontSize="small"
          sx={{ color: "rgba(76, 175, 80, 0.6)" }} // subtle green
        />
      ) : (
        <Close
          fontSize="small"
          sx={{ color: "rgba(158, 158, 158, 0.6)" }} // subtle gray
        />
      ),
  },
  {
    field: "follow_lab",
    flex: 2.5,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Follow <br /> Lab
      </div>
    ),
    renderCell: (params) =>
      params.value ? (
        <Check
          fontSize="small"
          sx={{ color: "rgba(76, 175, 80, 0.6)" }} // subtle green
        />
      ) : (
        <Close
          fontSize="small"
          sx={{ color: "rgba(158, 158, 158, 0.6)" }} // subtle gray
        />
      ),
  },
  {
    field: "lab_min",
    flex: 2.5,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Lab <br /> Min Val
      </div>
    ),
  },
  {
    field: "lab_max",
    flex: 2.5,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Lab <br /> Max Val
      </div>
    ),
  },
  {
    field: "min_val",
    flex: 2.5,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Clinician <br /> Min Val
      </div>
    ),
  },
  {
    field: "max_val",
    flex: 2.5,
    align: "center",
    headerAlign: "center",
    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Clinician <br /> Max Val
      </div>
    ),
  },
  {
    field: "previous_check",
    flex: 3.5,

    renderHeader: () => (
      <div style={{ textAlign: "center", whiteSpace: "normal" }}>
        Previous <br /> Test Result
      </div>
    ),
  },
  {
    field: "days_or_tests",
    headerName: "Days / Results",
    flex: 2.5,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "pdiv_below",
    headerName: "% Below",
    flex: 2,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "pdiv_above",
    headerName: "% Above",
    flex: 2,
    align: "center",
    headerAlign: "center",
  },
  { field: "check_type", headerName: "Keyword Rule", flex: 2.7 },
  { field: "keyword", headerName: "Keywords", flex: 4.5 },
  { field: "file_method", headerName: "File", flex: 3 },
  {
    field: "latest_100_days",
    headerName: "Latest 100 day records",
    flex: 5,
    renderCell: (params) => {
      const category = params.row.category_name;

      // Choose correct summary set
      const summarySet =
        params.row.enabled === "Enabled"
          ? summaries?.enabled_summary
          : summaries?.full_summary;

      const summary = summarySet?.[category];

      if (!summary) return "";

      const text = `Filed: ${summary.filed} / ${summary.total} (${Math.round(
        summary["filed_%"],
      )}%)`;

      return (
        <span style={{ cursor: "pointer", color: "#1976d2" }}>{text}</span>
      );
    },
  },
];

export default function FilingRuleFrame() {
  const dispatch = useDispatch();

  const [openAnalysisDialog, setOpenAnalysisDialog] = useState(false);
  const skipAnalysis = useSelector((state) => state.filingRules.skipAnalysis);

  const summaries = useSelector((state) => state.filingRules.summaries);

  const navigate = useNavigate();
  const { rules, loading, selected, updating, exporting } = useSelector(
    (state) => state.filingRules,
  );

  const [selectedFilingCategory, setSelectedFilingCategory] = useState(null);
  const [selectedEnabled, setSelectedEnabled] = useState(false);

  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );

  const has_write = useSelector(
    (state) => state.authen.loggedInUser?.license_role === "Change",
  );

  useEffect(() => {
    if (license_key) {
      dispatch(fetchFilingRules(license_key));
      dispatch(fetchLatest100DaySummaries(license_key));
    }
  }, [dispatch, license_key]);

  const handleRowDoubleClick = (params) => {
    dispatch(setSelectedCategory(params.row.category_name));
    navigate("/dashboard/pathology/Setup");
  };

  const handleSelection = (selectionModel) => {
    // Convert Set to Array
    const selectedIds = Array.from(selectionModel.ids);

    const selectedRowsData = selectedIds
      .map((id) => rules.find((r) => r.id === id))
      .filter(Boolean);

    dispatch(setSelected(selectedRowsData));
  };

  const [summaryText, setSummaryText] = useState("");

  const handleRowClick = (params) => {
    const category = params.row.category_name;

    setSelectedFilingCategory(category);
    setSelectedEnabled(params.row.enabled === "Enabled");
    // Choose correct summary set
    const summarySet =
      params.row.enabled === "Enabled"
        ? summaries?.enabled_summary
        : summaries?.full_summary;

    const data = summarySet?.[category];

    if (!data) return;

    const text =
      `${category}: ` +
      `Total ${data.total} | ` +
      `Filed ${data["filed_%"].toFixed(1)}% | ` +
      `Out of Range ${data["not_filed_result_out_of_range_%"].toFixed(1)}% | ` +
      `Rules Missing ${data["not_filed_rules_not_setup_%"].toFixed(1)}% | ` +
      `Clinician Filed ${data["not_filed_already_filed_by_clinician_%"].toFixed(1)}% | ` +
      `Inconclusive ${data["not_filed_inconclusive_report_%"].toFixed(1)}%`;

    setSummaryText(text);
  };
  const handleEnable = () => {
    if (selected.length > 0 && license_key) {
      const categories = [...new Set(selected.map((row) => row.category_name))];
      dispatch(
        updateFilingRuleStatus({
          categories,
          status: "Enabled",
          license_key,
        }),
      );
    }
  };

  const handleDisable = () => {
    if (selected.length > 0 && license_key) {
      const categories = [...new Set(selected.map((row) => row.category_name))];
      dispatch(
        updateFilingRuleStatus({
          categories,
          status: "Disabled",
          license_key,
        }),
      );
    }
  };

  const handleDelete = () => {
    if (!selected.length || !license_key) return;

    const categories = [...new Set(selected.map((row) => row.category_name))];

    const result = window.confirm(
      `Are you sure you want to delete the selected filing rules?\n\nCategories:\n${categories.join(
        ", ",
      )}`,
    );

    if (result) {
      dispatch(
        deleteFilingRuleStatus({
          categories,
          rows_data: selected,
          license_key,
        }),
      );
    } else {
      window.alert("The action has been cancelled.");
    }
  };

  const handleFilingAnalysis = () => {
    if (!selectedFilingCategory || !license_key) return;

    dispatch(
      fetchSkipAnalysis({
        category: selectedFilingCategory,
        removeNotSetup: selectedEnabled,
        license_key,
      }),
    );

    setOpenAnalysisDialog(true);
  };

  const handleExportToExcel = () => {
    if (license_key) {
      dispatch(exportFilingRulesToExcel(license_key));
    }
  };

  return (
    <Box
      sx={{
        height: "calc(100vh - 120px)", // adjust if you have a header/navbar
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Paper
        elevation={2}
        sx={{
          p: 2,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden", // important for sticky behavior
        }}
      >
        {/* Sticky Action Bar */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 3,
            backgroundColor: "background.paper",
            pb: 1,
            mb: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              pointerEvents: has_write ? "auto" : "none",
              opacity: has_write ? 1 : 0.3,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleEnable}
              disabled={selected.length === 0 || updating}
            >
              Enable
            </Button>

            <Button
              variant="contained"
              color="warning"
              onClick={handleDisable}
              disabled={selected.length === 0 || updating}
            >
              Disable
            </Button>

            <Button
              variant="contained"
              color="secondary"
              onClick={handleDelete}
              disabled={selected.length === 0 || updating}
            >
              Delete
            </Button>
            <Button
              variant="outlined"
              onClick={handleExportToExcel}
              disabled={exporting || loading}
            >
              {exporting ? "Exporting..." : "Export to Excel"}
            </Button>
            <Button
              variant="contained"
              color="info"
              onClick={handleFilingAnalysis}
              disabled={!selectedFilingCategory}
            >
              Filing Analysis
            </Button>
          </Stack>
        </Box>

        {summaryText && (
          <Box sx={{ mt: 2 }}>
            <Paper sx={{ p: 2 }}>{summaryText}</Paper>
          </Box>
        )}

        {/* Grid Container (Scrollable) */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              variant="dense"
              rows={rules}
              columns={getColumns(summaries)}
              pageSize={100}
              checkboxSelection
              disableRowSelectionOnClick
              autoHeight
              onRowSelectionModelChange={handleSelection}
              onRowDoubleClick={handleRowDoubleClick}
              onRowClick={handleRowClick}
              getRowId={(row) => row.id}
              loading={updating}
              getRowClassName={(params) => {
                const evenOdd =
                  params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
                const status = params.row.enabled;

                if (status === "Enabled") return `${evenOdd} bg-green`;
                if (status === "In Review") return `${evenOdd} bg-orange`;
                if (status === "Not Configured") return `${evenOdd} bg-pink`;
                return `${evenOdd} bg-red`;
              }}
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeaders": {
                  marginBottom: "30px",
                },

                "& .MuiDataGrid-virtualScroller": {
                  flex: "2700px !important",
                },
              }}
            />
          )}
        </Box>
      </Paper>

      <Dialog
        open={openAnalysisDialog}
        onClose={() => setOpenAnalysisDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Skip Analysis Summary — {selectedFilingCategory}
          <IconButton
            aria-label="close"
            onClick={() => setOpenAnalysisDialog(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <DataGrid
            autoHeight
            rows={skipAnalysis}
            columns={[
              { field: "skip_type", headerName: "Skip Type", flex: 4 },
              { field: "count", headerName: "Count", flex: 1 },
            ]}
            getRowId={(row) => row.skip_type}
            pageSize={20}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
